import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Images } from 'lucide-react';
import { projectsData } from '../../data/projects';

const AUTO_MS = 4000;

function HomeProjectsSlider() {
  const n = projectsData.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const headingId = useId();
  const liveId = `${headingId}-live`;
  const dotNavRef = useRef(null);

  const go = useCallback(
    (delta) => {
      setIndex((i) => {
        const next = i + delta;
        if (next < 0) return n - 1;
        if (next >= n) return 0;
        return next;
      });
    },
    [n]
  );

  const goTo = useCallback((i) => {
    setIndex(((i % n) + n) % n);
  }, [n]);

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % n);
    }, AUTO_MS);
    return () => window.clearInterval(id);
  }, [paused, index, n]);

  useEffect(() => {
    const nav = dotNavRef.current;
    if (!nav) return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    const active = nav.querySelector(`[data-slider-dot="${index}"]`);
    if (!active) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    active.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: reduce ? 'auto' : 'smooth',
    });
  }, [index]);

  const current = projectsData[index];

  return (
    <section
      className="relative py-14 md:py-16 bg-surface overflow-hidden border-t border-white/[0.06]"
      aria-labelledby={headingId}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
    >
      <div
        className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-brand-orange/10 blur-[80px] motion-reduce:hidden"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-16 left-10 h-56 w-56 rounded-full bg-brand-yellow/8 blur-[70px] motion-reduce:hidden"
        aria-hidden
      />

      <div className="container relative z-[1] mx-auto px-4">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-8 md:mb-10">
          <div>
            <p className="text-brand-amber text-xs font-semibold uppercase tracking-[0.2em] mb-2">
              Portfolio
            </p>
            <h2
              id={headingId}
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight"
            >
              Featured projects
            </h2>
            {/* <p className="mt-3 text-gray-400 text-sm md:text-base max-w-xl leading-relaxed">
              Real Mumbai installs—slides every 4 seconds. Use arrows or dots; hover or focus inside this
              section to pause.
            </p> */}
          </div>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 self-start md:self-auto rounded-full border border-white/15 bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-white hover:border-brand-amber/50 hover:bg-brand-orange/10 hover:text-brand-amber transition-all duration-300 shrink-0"
          >
            <Images className="w-4 h-4 opacity-80" aria-hidden />
            View all projects
          </Link>
        </div>
      </div>

      <div
        className="relative mx-auto w-full max-w-6xl px-4 md:px-6"
        aria-roledescription="carousel"
        aria-label="Project slideshow"
      >
        <div
          id={liveId}
          className="sr-only"
          aria-live="polite"
          aria-atomic="true"
        >
          Slide {index + 1} of {n}: {current.name}
        </div>

        <div className="relative rounded-2xl border border-white/10 bg-surface-elevated shadow-card-premium overflow-hidden group/slider">
          <div className="relative aspect-[4/3] md:aspect-[21/9] md:min-h-[280px]">
            <div
              className="flex h-full motion-safe:transition-[transform] motion-safe:duration-[700ms] motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
              style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}
            >
              {projectsData.map((project, slideIndex) => (
                <article
                  key={project.id}
                  className="relative h-full w-full shrink-0 overflow-hidden"
                  aria-hidden={slideIndex !== index}
                >
                  <div className="absolute inset-0 overflow-hidden">
                    <img
                      key={slideIndex === index ? `kb-${index}` : `idle-${project.id}`}
                      src={project.imageUrl}
                      alt={slideIndex === index ? project.name : ''}
                      className={[
                        'h-full w-full object-cover motion-safe:will-change-transform',
                        slideIndex === index
                          ? 'motion-safe:animate-ken-burns motion-reduce:animate-none'
                          : '',
                      ].join(' ')}
                      style={{ animationPlayState: paused ? 'paused' : 'running' }}
                      draggable={false}
                    />
                  </div>
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black via-black/55 via-40% to-transparent pointer-events-none"
                    aria-hidden
                  />
                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 md:p-10 pointer-events-none">
                    <div
                      key={slideIndex === index ? `active-${index}` : `idle-${project.id}`}
                      className={[
                        'max-w-3xl',
                        slideIndex === index
                          ? 'motion-safe:animate-fade-up motion-reduce:animate-none'
                          : 'opacity-0',
                      ].join(' ')}
                    >
                      <p className="text-brand-amber/95 text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] mb-2 drop-shadow-md">
                        Case study · Mumbai
                      </p>
                      <h3 className="text-white text-lg sm:text-xl md:text-2xl font-bold leading-snug line-clamp-3 drop-shadow-lg">
                        {project.name}
                      </h3>
                      <p className="mt-2 text-gray-300 text-xs sm:text-sm line-clamp-2 md:line-clamp-3 max-w-2xl leading-relaxed drop-shadow-md hidden sm:block">
                        {project.shortDesc}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 md:px-5 md:py-3.5 bg-black/40 border-t border-white/[0.06]">
            <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
              <div
                key={index}
                className={[
                  'h-full w-full origin-left rounded-full bg-gradient-to-r from-brand-orange via-brand-amber to-brand-yellow',
                  paused ? 'motion-reduce:scale-x-100' : 'motion-safe:animate-slider-progress motion-reduce:!scale-x-100 motion-reduce:animate-none',
                ].join(' ')}
                style={{ animationPlayState: paused ? 'paused' : 'running' }}
                aria-hidden
              />
            </div>
            <span className="text-[10px] font-mono text-gray-500 tabular-nums shrink-0">
              {String(index + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}
            </span>
          </div>

          <button
            type="button"
            onClick={() => go(-1)}
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white backdrop-blur-sm opacity-90 transition-all duration-300 hover:bg-brand-orange/90 hover:border-brand-amber hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-amber md:opacity-0 md:pointer-events-none md:group-hover/slider:opacity-90 md:group-hover/slider:pointer-events-auto"
            aria-label="Previous project"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white backdrop-blur-sm opacity-90 transition-all duration-300 hover:bg-brand-orange/90 hover:border-brand-amber hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-amber md:opacity-0 md:pointer-events-none md:group-hover/slider:opacity-90 md:group-hover/slider:pointer-events-auto"
            aria-label="Next project"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" aria-hidden />
          </button>
        </div>

        <div
          className="mt-6 flex flex-wrap items-center justify-center gap-2 md:gap-2.5"
          role="tablist"
          aria-label="Choose project slide"
        >
          {projectsData.map((project, i) => (
            <button
              key={project.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Show project ${i + 1}: ${project.name}`}
              onClick={() => goTo(i)}
              className={[
                'rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-amber focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                i === index
                  ? 'h-2.5 w-8 bg-gradient-to-r from-brand-orange to-brand-yellow shadow-[0_0_16px_-2px_rgba(234,88,12,0.5)]'
                  : 'h-2 w-2 bg-white/25 hover:bg-white/45 hover:scale-110',
              ].join(' ')}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default HomeProjectsSlider;
